/**
 * Iconos de trazo del sidebar, del mismo estilo que los del prediseño
 * (Modulo-Economia.html): rejilla de 24×24, sin relleno y con trazo
 * redondeado, que pinta el CSS del menú (`.menu-svg-icon`). Los trazos siguen
 * los de Lucide (licencia ISC), que es la familia que usa el prediseño.
 *
 * Los usan el menú y `<app-bo-icon name="…">` (shared/components/bo-icon) en
 * el resto de pantallas.
 *
 * Cada icono es una lista de atributos `d`, uno por <path>, como en Lucide: van
 * separados porque un trazo que empieza por `m` minúscula sería relativo al
 * anterior si se unieran. Círculos y rectángulos se convierten a path con los
 * helpers de abajo. En `menu-config.ts` se usan como
 * `icon: { type: 'svg', name: '<clave>' }`.
 */

const circle = (cx: number, cy: number, r: number): string =>
  `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`;

const rect = (x: number, y: number, w: number, h: number, rx = 0): string =>
  `M${x + rx} ${y}h${w - 2 * rx}a${rx} ${rx} 0 0 1 ${rx} ${rx}v${h - 2 * rx}` +
  `a${rx} ${rx} 0 0 1 ${-rx} ${rx}h${-(w - 2 * rx)}a${rx} ${rx} 0 0 1 ${-rx} ${-rx}` +
  `v${-(h - 2 * rx)}a${rx} ${rx} 0 0 1 ${rx} ${-rx}z`;

const icon = (...parts: (string | string[])[]): string[] => parts.flat();

const SHIELD =
  'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z';
const CLIPBOARD: string[] = icon(
  rect(8, 2, 8, 4, 1),
  'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
);
const FILE =
  'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z M14 2v4a2 2 0 0 0 2 2h4';

export const MENU_SVG_ICONS: Record<string, string[]> = {
  // Acciones e interfaz (tablero, perfil…), mismo estilo que los del menú
  'arrow-right': icon('M5 12h14', 'm12 5 7 7-7 7'),
  'circle-plus': icon(circle(12, 12, 10), 'M8 12h8', 'M12 8v8'),
  'circle-alert': icon(circle(12, 12, 10), 'M12 8v4', 'M12 16h.01'),
  'arrow-up-down': icon('m21 16-4 4-4-4', 'M17 20V4', 'm3 8 4-4 4 4', 'M7 4v16'),
  'arrow-up': icon('m5 12 7-7 7 7', 'M12 19V5'),
  'arrow-down': icon('M12 5v14', 'm19 12-7 7-7-7'),
  'chevron-right': icon('m9 18 6-6-6-6'),
  // Botón de plegar/desplegar el menú lateral (sidebar-logo)
  'chevron-left': icon('m15 18-6-6 6-6'),
  database: icon(
    'M3 5a9 3 0 1 0 18 0a9 3 0 1 0-18 0',
    'M3 5v14a9 3 0 0 0 18 0V5',
    'M3 12a9 3 0 0 0 18 0',
  ),
  clock: icon(circle(12, 12, 10), 'M12 6v6l4 2'),
  ban: icon(circle(12, 12, 10), 'm4.9 4.9 14.2 14.2'),
  eye: icon(
    'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
    circle(12, 12, 3),
  ),
  pencil: icon(
    'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
    'm15 5 4 4',
  ),
  repeat: icon(
    'm17 2 4 4-4 4',
    'M3 11v-1a4 4 0 0 1 4-4h14',
    'm7 22-4-4 4-4',
    'M21 13v1a4 4 0 0 1-4 4H3',
  ),
  calendar: icon('M8 2v4', 'M16 2v4', rect(3, 4, 18, 18, 2), 'M3 10h18'),
  'refresh-cw': icon(
    'M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8',
    'M21 3v5h-5',
    'M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16',
    'M8 16H3v5',
  ),
  'trash-2': icon(
    'M3 6h18',
    'M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6',
    'M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2',
    'M10 11v6',
    'M14 11v6',
  ),
  globe: icon(
    circle(12, 12, 10),
    'M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20',
    'M2 12h20',
  ),
  'log-out': icon(
    'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4',
    'm16 17 5-5-5-5',
    'M21 12H9',
  ),
  'chevron-down': icon('m6 9 6 6 6-6'),
  'chevron-up': icon('m18 15-6-6-6 6'),

  // General
  'layout-grid': icon(
    rect(3, 3, 7, 7, 1),
    rect(14, 3, 7, 7, 1),
    rect(14, 14, 7, 7, 1),
    rect(3, 14, 7, 7, 1),
  ),
  house: icon(
    'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8',
    'M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  ),

  // Actividad
  map: icon(
    'M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z',
    'M15 5.764v15',
    'M9 3.236v15',
  ),
  users: icon(
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    circle(9, 7, 4),
    'M22 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75',
  ),
  'shield-alert': icon(SHIELD, 'M12 8v4', 'M12 16h.01'),
  // Retiradas: billete con flecha de salida (dinero que sale de WAIIS).
  'banknote-arrow-up': icon(
    'M12 18H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5',
    'M18 12h.01 M6 12h.01',
    circle(12, 12, 2),
    'M19 22v-6',
    'm22 19-3-3-3 3',
  ),
  headphones: icon(
    'M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3',
  ),

  // Dinero
  'line-chart': icon('M3 3v18h18 M7 15l4-4 3 3 5-6'),

  // Empresas y usuarios
  building: icon(
    'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z',
    'M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2',
    'M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2',
    'M10 6h4 M10 10h4 M10 14h4 M10 18h4',
  ),
  'user-round': icon(circle(12, 8, 5), 'M20 21a8 8 0 0 0-16 0'),
  contact: icon(
    'M16 2v2 M8 2v2',
    'M7 22v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2',
    circle(12, 11, 3),
    rect(3, 4, 18, 18, 2),
  ),
  'share-2': icon(
    circle(18, 5, 3),
    circle(6, 12, 3),
    circle(18, 19, 3),
    'M8.59 13.51l6.83 3.98 M15.41 6.51l-6.82 3.98',
  ),
  lock: icon(rect(3, 11, 18, 11, 2), 'M7 11V7a5 5 0 0 1 10 0v4'),

  // Créditos
  briefcase: icon(
    'M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16',
    rect(2, 6, 20, 14, 2),
  ),
  gift: icon(
    rect(3, 8, 18, 4, 1),
    'M12 8v13',
    'M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7',
    'M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5',
  ),
  ticket: icon(
    'M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z',
    'M13 5v2 M13 17v2 M13 11v2',
  ),
  'badge-percent': icon(
    'M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z',
    'm15 9-6 6 M9 9h.01 M15 15h.01',
  ),

  // Parkings
  'square-parking': icon(rect(3, 3, 18, 18, 2), 'M9 17V7h4a3 3 0 0 1 0 6H9'),
  'calendar-check': icon(
    'M8 2v4 M16 2v4',
    rect(3, 4, 18, 18, 2),
    'M3 10h18',
    'm9 16 2 2 4-4',
  ),
  coins: icon(
    circle(8, 8, 6),
    'M18.09 10.37A6 6 0 1 1 10.34 18',
    'M7 6h1v4',
    'm16.71 13.88.7.71-2.82 2.82',
  ),
  // Volante: el conductor en el desplegable del coche de Usuario › Viajes.
  'steering-wheel': icon(
    circle(12, 12, 10),
    circle(12, 12, 2),
    'M12 14v8',
    'M10.1 11.4 2.4 9.6',
    'm13.9 11.4 7.7-1.8',
  ),
  // Saquito de dinero (no hay en Lucide): lo que paga o cobra cada persona del coche.
  'money-bag': icon(
    'M9 3h6l-1.6 4h-2.8z',
    'M10.4 7C6.6 9.4 4 12.6 4 16a5 5 0 0 0 5 5h6a5 5 0 0 0 5-5c0-3.4-2.6-6.6-6.4-9',
    'M14.2 12.3a3 3 0 1 0 0 4.4',
    'M9.5 13.8h3.5 M9.5 15.4h3.5',
  ),

  // Pooling, huella y revisiones
  activity: icon('M22 12h-4l-3 9L9 3l-3 9H2'),
  fingerprint: icon(
    'M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4',
    'M14 13.12c0 2.38 0 6.38-1 8.88',
    'M17.29 21.02c.12-.6.43-2.3.5-3.02',
    'M2 12a10 10 0 0 1 18-6',
    'M2 16h.01',
    'M21.8 16c.2-2 .131-5.354 0-6',
    'M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2',
    'M8.65 22c.21-.66.45-1.32.57-2',
    'M9 6.8a6 6 0 0 1 9 5.2v2',
  ),
  'clipboard-check': icon(CLIPBOARD, 'm9 14 2 2 4-4'),
  signpost: icon(
    'M12 13v8 M12 3v3',
    'M4 6a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h13a2 2 0 0 0 1.152-.365l3.424-2.317a1 1 0 0 0 0-1.635l-3.424-2.318A2 2 0 0 0 17 6z',
  ),

  // Etiquetas de huella (puntos y segmentos): misma etiqueta, por partida
  // doble, para que se parezca a `tag` pero se distinga en el menú.
  tags: icon(
    'm15 5 6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19',
    'M9.586 5.586A2 2 0 0 0 8.172 5H3a1 1 0 0 0-1 1v5.172a2 2 0 0 0 .586 1.414L8.29 18.29a2.426 2.426 0 0 0 3.42 0l3.58-3.58a2.426 2.426 0 0 0 0-3.42z',
    circle(6.5, 9.5, 0.5),
  ),

  // App y match
  star: icon(
    'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  ),
  // Etiquetas de la app (usuarios y rutas): una etiqueta.
  tag: icon(
    'M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z',
    circle(7.5, 7.5, 0.5),
  ),
  link: icon(
    'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71',
    'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  ),
  list: icon('M3 6h.01 M3 12h.01 M3 18h.01 M8 6h13 M8 12h13 M8 18h13'),
  'sliders-horizontal': icon(
    'M21 4h-7 M10 4H3 M21 12h-9 M8 12H3 M21 20h-5 M12 20H3 M14 2v4 M8 10v4 M16 18v4',
  ),
  'map-pin': icon(
    'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
    circle(12, 10, 3),
  ),

  // Configuración
  'credit-card': icon(rect(2, 5, 20, 14, 2), 'M2 10h20'),
  percent: icon('M19 5 5 19', circle(6.5, 6.5, 2.5), circle(17.5, 17.5, 2.5)),
  'sliders-vertical': icon(
    'M4 21v-7 M4 10V3 M12 21v-9 M12 8V3 M20 21v-5 M20 12V3 M2 14h4 M10 8h4 M18 16h4',
  ),
  'file-text': icon(FILE, 'M10 9H8 M16 13H8 M16 17H8'),
  'file-pen': icon(
    'm18 5-2.414-2.414A2 2 0 0 0 14.172 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2',
    'M21.378 12.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z',
    'M8 18h1',
  ),
  'shield-check': icon(SHIELD, 'm9 12 2 2 4-4'),
  target: icon(circle(12, 12, 10), circle(12, 12, 6), circle(12, 12, 2)),
  'clipboard-list': icon(CLIPBOARD, 'M12 11h4 M12 16h4 M8 11h.01 M8 16h.01'),
  'list-checks': icon(
    'm3 17 2 2 4-4',
    'm3 7 2 2 4-4',
    'M13 6h8 M13 12h8 M13 18h8',
  ),
  car: icon(
    'M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2',
    circle(7, 17, 2),
    'M9 17h6',
    circle(17, 17, 2),
  ),
  // Detalle de viaje (pages/travel/pooling-route-detail)
  check: icon('M20 6 9 17l-5-5'),
  x: icon('M18 6 6 18', 'm6 6 12 12'),
  plus: icon('M5 12h14', 'M12 5v14'),
  'circle-check': icon(circle(12, 12, 10), 'm9 12 2 2 4-4'),
  'circle-x': icon(circle(12, 12, 10), 'm15 9-6 6', 'm9 9 6 6'),
  'circle-help': icon(
    circle(12, 12, 10),
    'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3',
    'M12 17h.01',
  ),
  'triangle-alert': icon(
    'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3',
    'M12 9v4',
    'M12 17h.01',
  ),
  hourglass: icon(
    'M5 22h14',
    'M5 2h14',
    'M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22',
    'M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2',
  ),
  copy: icon(
    rect(8, 8, 14, 14, 2),
    'M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2',
  ),
  'arrow-left': icon('m12 19-7-7 7-7', 'M19 12H5'),
  'message-circle': icon('M7.9 20A9 9 0 1 0 4 16.1L2 22Z'),
  'rotate-ccw': icon(
    'M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8',
    'M3 3v5h5',
  ),
  wallet: icon(
    'M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1',
    'M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4',
  ),
  // Historial de agentes (pages/waiis/agent-conversations): canales, agente y resultado
  smartphone: icon(rect(5, 2, 14, 20, 2), 'M12 18h.01'),
  phone: icon(
    'M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384',
  ),
  bot: icon(
    'M12 8V4H8',
    rect(4, 8, 16, 12, 2),
    'M2 14h2',
    'M20 14h2',
    'M15 13v2',
    'M9 13v2',
  ),
  search: icon(circle(11, 11, 8), 'm21 21-4.3-4.3'),
  'external-link': icon(
    'M15 3h6v6',
    'M10 14 21 3',
    'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
  ),
  'circle-minus': icon(circle(12, 12, 10), 'M8 12h8'),
  'circle-arrow-out-up-right': icon('M22 12A10 10 0 1 1 12 2', 'M22 2 12 12', 'M16 2h6v6'),
  'messages-square': icon(
    'M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z',
    'M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1',
  ),
  // Chat de soporte: adjuntos, envío, leído, bandeja vacía y avisos de captura.
  image: icon(
    rect(3, 3, 18, 18, 2),
    circle(9, 9, 2),
    'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21',
  ),
  send: icon(
    'M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z',
    'm21.854 2.147-10.94 10.939',
  ),
  inbox: icon(
    'M22 12h-6l-2 3h-4l-2-3H2',
    'M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
  ),
  'maximize-2': icon('M15 3h6v6', 'm21 3-7 7', 'm3 21 7-7', 'M9 21H3v-6'),
  mail: icon(rect(2, 4, 20, 16, 2), 'm22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7'),
  'mail-open': icon(
    'M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6Z',
    'm22 10-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 10',
  ),
  // Chat de soporte: aviso de intento de captura (pantallazo / grabación).
  camera: icon(
    'M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z',
    circle(12, 13, 3),
  ),
  video: icon(
    'm16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5',
    rect(2, 6, 14, 12, 2),
  ),
  'video-off': icon(
    'M10.66 6H14a2 2 0 0 1 2 2v2.5l5.248-3.062A.5.5 0 0 1 22 7.87v8.196',
    'M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2',
    'm2 2 20 20',
  ),
  'check-check': icon('M18 6 7 17l-5-5', 'm22 10-7.5 7.5L13 16'),
  // Ficha de empresa: KPIs (en carpooling, CO₂e evitado) y descarga de documentos PMST.
  'user-check': icon(
    'm16 11 2 2 4-4',
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    circle(9, 7, 4),
  ),
  leaf: icon(
    'M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z',
    'M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12',
  ),
  'circle-dot': icon(circle(12, 12, 10), circle(12, 12, 1)),
  bus: icon(
    'M8 6v6',
    'M15 6v6',
    'M2 12h19.6',
    'M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3',
    circle(7, 18, 2),
    'M9 18h5',
    circle(16, 18, 2),
  ),
  receipt: icon(
    'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z',
    'M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8',
    'M12 17.5v-11',
  ),
  'undo-2': icon('M9 14 4 9l5-5', 'M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11'),
  folder: icon('M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z'),
  'calendar-clock': icon(
    'M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5',
    'M16 2v4',
    'M8 2v4',
    'M3 10h5',
    'M17.5 17.5 16 16.3V14',
    circle(16, 16, 6),
  ),
  'car-taxi-front': icon(
    'M10 2h4',
    'm21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8',
    'M7 14h.01',
    'M17 14h.01',
    rect(3, 10, 18, 8, 2),
    'M5 18v2',
    'M19 18v2',
  ),
  printer: icon(
    'M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6',
    'M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2',
    rect(6, 14, 12, 8, 1),
  ),
  download: icon(
    'M12 15V3',
    'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4',
    'm7 10 5 5 5-5',
  ),
  // Ficha de empresa › Incentivos: segmentos «Sin registrar» e «Inactivos».
  'user-plus': icon(
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    circle(9, 7, 4),
    'M19 8v6',
    'M22 11h-6',
  ),
  moon: icon('M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z'),
  // Empresas (indicadores de altas y nota informativa)
  bell: icon(
    'M10.268 21a2 2 0 0 0 3.464 0',
    'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
  ),
  info: icon(circle(12, 12, 10), 'M12 16v-4', 'M12 8h.01'),
  // Comunicado en la app (menú y vista previa)
  megaphone: icon('m3 11 18-5v12L3 14v-3z', 'M11.6 16.8a3 3 0 1 1-5.8-1.6'),
  'calendar-range': icon(
    rect(3, 4, 18, 18, 2),
    'M16 2v4 M3 10h18 M8 2v4',
    'M17 14h-6 M13 18H7 M7 14h.01 M17 18h.01',
  ),
  'calendar-days': icon(
    'M8 2v4 M16 2v4',
    rect(3, 4, 18, 18, 2),
    'M3 10h18',
    'M8 14h.01 M12 14h.01 M16 14h.01 M8 18h.01 M12 18h.01 M16 18h.01',
  ),
  // Puntos de interés (pestaña de subida de fichero)
  upload: icon('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'),
  // Usuarios de marketing (embudo y permisos)
  route: icon(circle(6, 19, 3), 'M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15', circle(18, 5, 3)),
  navigation: icon('M3 11l19-9-9 19-2-8-8-2z'),
  bluetooth: icon('m7 7 10 10-5 5V2l5 5L7 17'),
  filter: icon('M22 3H2l8 9.46V19l4 2v-8.54L22 3z'),
  // Sello de verificación (ficha de usuario, junto al nombre)
  'badge-check': icon(
    'M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z',
    'm9 12 2 2 4-4',
  ),
  // Revisión de corredores («Ocultar todos»)
  'eye-off': icon(
    'M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49',
    'M14.084 14.158a3 3 0 0 1-4.242-4.242',
    'M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143',
    'm2 2 20 20',
  ),
  // Pantalla de escritorio (pestaña «Dashboard» de Términos y condiciones)
  monitor: icon(rect(2, 3, 20, 14, 2), 'M8 21h8', 'M12 17v4'),
  // Chat de soporte (WS-2271): cabecera, composer, panel de contexto y
  // respuestas rápidas / recordatorios
  ellipsis: icon(circle(12, 12, 1), circle(19, 12, 1), circle(5, 12, 1)),
  'panel-right': icon(rect(3, 3, 18, 18, 2), 'M15 3v18'),
  zap: icon(
    'M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z',
  ),
  clipboard: CLIPBOARD,
  'cloud-off': icon(
    'm2 2 20 20',
    'M5.782 5.782A7 7 0 0 0 9 19h8.5a4.5 4.5 0 0 0 1.307-.193',
    'M21.532 16.5A4.5 4.5 0 0 0 17.5 10h-1.79A7.008 7.008 0 0 0 10 5.07',
  ),
  settings: icon(
    'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
    circle(12, 12, 3),
  ),
  lightbulb: icon(
    'M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5',
    'M9 18h6',
    'M10 22h4',
  ),
  reply: icon('m9 17-5-5 5-5', 'M20 18v-2a4 4 0 0 0-4-4H4'),
  'grip-vertical': icon(
    circle(9, 12, 1),
    circle(9, 5, 1),
    circle(9, 19, 1),
    circle(15, 12, 1),
    circle(15, 5, 1),
    circle(15, 19, 1),
  ),
  landmark: icon(
    'M10 18v-7',
    'M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z',
    'M14 18v-7',
    'M18 18v-7',
    'M3 22h18',
    'M6 18v-7',
  ),
  'id-card': icon('M16 10h2', 'M16 14h2', 'M6.17 15a3 3 0 0 1 5.66 0', circle(9, 11, 2), rect(2, 5, 20, 14, 2)),
  'user-x': icon('M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', circle(9, 7, 4), 'm17 8 5 5', 'm22 8-5 5'),
  'octagon-alert': icon(
    'M12 16h.01',
    'M12 8v4',
    'M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z',
  ),
  'message-square-text': icon('M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z', 'M13 8H7', 'M17 12H7'),
  'message-square-more': icon(
    'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
    'M8 10h.01',
    'M12 10h.01',
    'M16 10h.01',
  ),
  sparkles: icon(
    'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z',
    'M20 3v4',
    'M22 5h-4',
    'M4 17v2',
    'M5 18H3',
  ),
  languages: icon('m5 8 6 6', 'm4 14 6-6 2-3', 'M2 5h12', 'M7 2h1', 'm22 22-5-10-5 10', 'M14 18h6'),
  // Configuración › Reglas de fraude: el escudo de Fraude (shield-alert) con
  // líneas de texto dentro = «las reglas del fraude».
  'shield-rules': icon(SHIELD, 'M9 10h6', 'M9 14h4'),
  // Configuración › Comisiones: Lucide «circle-percent».
  'circle-percent': icon(circle(12, 12, 10), 'm15 9-6 6', 'M9 9h.01', 'M15 15h.01'),
  // Configuración › Verificación facial: Lucide «scan-face».
  'scan-face': icon(
    'M3 7V5a2 2 0 0 1 2-2h2',
    'M17 3h2a2 2 0 0 1 2 2v2',
    'M21 17v2a2 2 0 0 1-2 2h-2',
    'M7 21H5a2 2 0 0 1-2-2v-2',
    'M8 14s1.5 2 4 2 4-2 4-2',
    'M9 9h.01',
    'M15 9h.01',
  ),
  // Comunicado en la app: «Ver HTML» del editor del cuerpo.
  code: icon('m16 18 6-6-6-6', 'm8 6-6 6 6 6'),
  // Marketing › Correos: vista previa en claro (Lucide «sun»; el oscuro es `moon`).
  sun: icon(
    circle(12, 12, 4),
    'M12 2v2',
    'M12 20v2',
    'm4.93 4.93 1.41 1.41',
    'm17.66 17.66 1.41 1.41',
    'M2 12h2',
    'M20 12h2',
    'm6.34 17.66-1.41 1.41',
    'm19.07 4.93-1.41 1.41',
  ),
};
